import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { profileOptionsQuery } from "@amber/collection/ui";
import { inputClass } from "@amber/ui";
import { searchQuery } from "./queries";
import type { SearchPageSearch } from "./params";
import { PLACEHOLDER } from "./hits";
import { SearchFilters } from "./SearchFilters";
import { SearchResults } from "./SearchResults";

const PAGE_LIMIT = 50;

export function SearchPage({
  search,
  onSearch,
}: {
  search: SearchPageSearch;
  onSearch: (next: SearchPageSearch) => void;
}) {
  const q = search.q?.trim() ?? "";
  const [draft, setDraft] = useState(q);
  useEffect(() => {
    setDraft(q);
  }, [q]);
  const { q: _q, ...filters } = search;
  const result = useQuery(searchQuery({ ...filters, q }, PAGE_LIMIT));
  const people = useQuery(profileOptionsQuery);
  const data = result.data?.q === q ? result.data : undefined;
  const set = (patch: SearchPageSearch) => {
    onSearch(Object.fromEntries(Object.entries({ ...search, ...patch }).filter(([, v]) => v !== undefined)));
  };
  const personName = people.data?.profiles.find((p) => p.id === search.person)?.name;
  const filtered = Boolean(search.person ?? search.language ?? search.readiness ?? search.corrected);

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="mx-auto max-w-[960px] px-6 py-5 max-narrow:px-3">
        <form
          role="search"
          aria-label="Search the workspace"
          onSubmit={(e) => {
            e.preventDefault();
            set({ q: draft.trim() || undefined });
          }}
          className="mb-3 flex items-center gap-2"
        >
          <h1 className="mr-2 text-2xl font-semibold">Search</h1>
          <input
            type="search"
            aria-label="Search terms"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            placeholder={PLACEHOLDER}
            className={inputClass("max-w-[480px] flex-1 px-2.5")}
          />
        </form>
        <SearchFilters search={search} set={set} profiles={people.data?.profiles} personName={personName} />
        <SearchResults q={q} data={data} filtered={filtered} />
      </div>
    </div>
  );
}
